import { useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook for extraction-related API calls and business logic.
 * Keeps App.js focused on state + layout.
 */
export function useExtractionApi() {
  const fetchStats = useCallback(async (setStats) => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchCountries = useCallback(async (continent, setCountries) => {
    try {
      const res = await axios.get(`${API}/countries/${continent}`);
      setCountries(res.data.countries || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchExtractionRuns = useCallback(async (setExtractionRuns) => {
    try {
      const res = await axios.get(`${API}/extraction-runs`);
      setExtractionRuns(res.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchCompletedExtractions = useCallback(async (setCompleted) => {
    try {
      const res = await axios.get(`${API}/completed-extractions`);
      setCompleted(res.data.completed || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchExtractionStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/extraction-status`);
      return res.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }, []);

  const checkHealth = useCallback(async (setDbHealth) => {
    try {
      const res = await axios.get(`${API}/health`);
      setDbHealth(res.data);
    } catch (e) {
      setDbHealth({ status: "error", database: "unreachable", message: "Backend API is not responding" });
    }
  }, []);

  const analyzeFirstLevel = useCallback(async (country) => {
    const res = await axios.get(`${API}/analyze-first-level/${encodeURIComponent(country)}`);
    if (res.data.cached) toast.info("Using cached analysis");
    return res.data;
  }, []);

  const analyzeSecondLevel = useCallback(async (country, firstLevelName) => {
    const res = await axios.get(`${API}/analyze-second-level/${encodeURIComponent(country)}/${encodeURIComponent(firstLevelName)}`);
    if (res.data.cached) toast.info("Using cached analysis");
    return res.data;
  }, []);

  const clearAnalysis = useCallback(async (country) => {
    await axios.post(`${API}/clear-analysis/${country}`);
    toast.success("Analysis cleared");
  }, []);

  const startExtraction = useCallback(async (payload) => {
    const res = await axios.post(`${API}/extract`, payload);
    return res.data;
  }, []);

  const retryExtraction = useCallback(async (runId) => {
    const res = await axios.post(`${API}/retry-extraction/${runId}`);
    return res.data;
  }, []);

  const fetchBeaches = useCallback(async (params) => {
    const res = await axios.get(`${API}/beaches`, { params });
    return res.data;
  }, []);

  const fetchDbCountries = useCallback(async () => {
    const res = await axios.get(`${API}/countries-list`);
    return res.data.countries || [];
  }, []);

  const fetchDbFirstLevels = useCallback(async (country) => {
    const params = country ? { country } : {};
    const res = await axios.get(`${API}/first-levels-list`, { params });
    return res.data.first_levels || [];
  }, []);

  const fetchDbSurfaces = useCallback(async () => {
    const res = await axios.get(`${API}/surfaces-list`);
    return res.data.surfaces || [];
  }, []);

  return useMemo(() => ({
    fetchStats, fetchCountries, fetchExtractionRuns,
    fetchCompletedExtractions, fetchExtractionStatus, checkHealth,
    analyzeFirstLevel, analyzeSecondLevel, clearAnalysis,
    startExtraction, retryExtraction,
    fetchBeaches, fetchDbCountries, fetchDbFirstLevels, fetchDbSurfaces,
  }), [
    fetchStats, fetchCountries, fetchExtractionRuns,
    fetchCompletedExtractions, fetchExtractionStatus, checkHealth,
    analyzeFirstLevel, analyzeSecondLevel, clearAnalysis,
    startExtraction, retryExtraction,
    fetchBeaches, fetchDbCountries, fetchDbFirstLevels, fetchDbSurfaces,
  ]);
}
